import { useEffect, type ReactNode } from 'react';
import { createRootRoute, createRoute, createRouter, Outlet, redirect, useNavigate, useRouterState } from '@tanstack/react-router';
import { localTokenStore } from './lib/api';
import { enterFor, justBooked, peekEnter, rewindTo } from './lib/nav';
import { useRide } from './lib/ride';
import { isOpen } from './lib/rideScreen';
import { tripSearch } from './lib/tripUrl';
import { Welcome } from './screens/Welcome';
import { Phone } from './screens/Phone';
import { Code } from './screens/Code';
import { Home } from './screens/Home';
import { Search } from './screens/Search';
import { ChooseRide } from './screens/ChooseRide';
import { ConfirmPickup } from './screens/ConfirmPickup';
import { LiveRide } from './screens/LiveRide';
import { Trips } from './screens/Trips';
import { Account } from './screens/Account';
import { Contacts } from './screens/Contacts';
import { SavedPlaces } from './screens/SavedPlaces';
import { Track } from './screens/Track';
import { ArtPreview } from './screens/ArtPreview';
import { PhoneShell, Shell } from './components/Shell';
import { useLayout } from './lib/layout';

const signedIn = () => !!localTokenStore.get();
const needAuth = () => {
  if (!signedIn()) throw redirect({ to: '/' });
};

/** The booking steps; while a ride is open they are behind it, and Back from the ride lands on Home. */
const BOOKING = new Set(['/search', '/choose', '/pickup']);

/**
 * An open ride owns the booking steps: Back from the live ride (or a refresh on an old booking step) goes to Home
 * — rewinding the history to the Home entry when it is there, so a second Back doesn't walk through them again.
 */
function useRideGuard(path: string): boolean {
  const { ride, loaded } = useRide();
  const nav = useNavigate();
  // (the ride just booked from Confirm pickup is on its way to /ride: that one screen doesn't count)
  const blocked = loaded && !!ride && isOpen(ride.status) && BOOKING.has(path) && !(path === '/pickup' && (justBooked() === '*' || ride.id === justBooked()));
  useEffect(() => {
    if (!blocked) return;
    if (!rewindTo('/home')) void nav({ to: '/home', replace: true });
  }, [blocked, nav]);
  return blocked;
}

function Frame() {
  // the screen actually on show (not the location the router is still loading), so a screen never remounts mid-navigation
  const path = useRouterState({ select: (r) => r.matches[r.matches.length - 1]?.pathname ?? r.location.pathname });
  const kind = enterFor(path);
  const L = useLayout();
  const blocked = useRideGuard(path);
  const route = (
    <div key={path} className={`zr-route zr-enter-${kind}`} data-enter={kind}>
      {!blocked && <Outlet />}
    </div>
  );
  // one shared map for the whole session: beside the panel on tablets and desktops, under the sheet on phones
  return L.wide ? <Shell path={path}>{route}</Shell> : <PhoneShell path={path}>{route}</PhoneShell>;
}

const root = createRootRoute({ component: Frame });
const r = (path: string, component: () => ReactNode, guard = true) =>
  createRoute({ getParentRoute: () => root, path, component, beforeLoad: guard ? needAuth : undefined });

const tree = root.addChildren([
  createRoute({
    getParentRoute: () => root,
    path: '/',
    component: Welcome,
    beforeLoad: () => {
      if (signedIn()) throw redirect({ to: '/home' });
    },
  }),
  r('/signin/phone', Phone, false),
  r('/signin/code', Code, false),
  r('/home', Home),
  createRoute({
    getParentRoute: () => root,
    path: '/search',
    component: Search,
    beforeLoad: needAuth,
    validateSearch: (s: Record<string, unknown>): { field?: 'pickup' | 'drop' | 'home' | 'work' | 'place' } => ({
      field: s.field === 'pickup' || s.field === 'home' || s.field === 'work' || s.field === 'place' ? s.field : undefined,
    }),
  }),
  // the trip is in the address (?from=&to=), so a refresh, a shared link or Back/Forward shows the same trip
  createRoute({ getParentRoute: () => root, path: '/choose', component: ChooseRide, beforeLoad: needAuth, validateSearch: tripSearch }),
  createRoute({
    getParentRoute: () => root,
    path: '/pickup',
    component: ConfirmPickup,
    beforeLoad: needAuth,
    validateSearch: (s: Record<string, unknown>) => ({
      ...tripSearch(s),
      pick: s.pick === 'drop' || s.pick === 'home' || s.pick === 'work' || s.pick === 'pickup' || s.pick === 'place' ? (s.pick as 'pickup' | 'drop' | 'home' | 'work' | 'place') : undefined,
    }),
  }),
  r('/ride', LiveRide),
  r('/trips', Trips),
  r('/account', Account),
  r('/account/contacts', Contacts),
  r('/account/places', SavedPlaces),
  r('/t/$token', Track, false),
  ...(import.meta.env.DEV ? [r('/__art', ArtPreview, false)] : []),
]);

const reduceMotion = () => typeof window !== 'undefined' && !!window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;

/**
 * Screen changes run as View Transitions where the browser has them (the sheet/panel morphs between steps, the
 * content crossfades, the shared map stays live); src/ui.css has the CSS enter animations as the fallback.
 * The type (push / back / fade / rise) picks the animation; a change of only the query string doesn't animate.
 */
export const router = createRouter({
  routeTree: tree,
  defaultPreload: false,
  scrollRestoration: false,
  defaultViewTransition: {
    types: ({ fromLocation, toLocation, pathChanged }) => {
      if (!pathChanged || !fromLocation || reduceMotion()) return false;
      const kind = peekEnter(toLocation.pathname);
      document.documentElement.dataset.nav = kind;
      return [kind];
    },
  },
});

declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router;
  }
}
